import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/common/PageHeader';
import { SectionCard } from '../components/common/SectionCard';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { Input } from '../components/ui/Input';
import { ScoreChip } from '../components/common/ScoreChip';
import { RecordStatusPill } from '../components/common/StatusPills';
import { usePlatform } from '../contexts/PlatformContext';
import { entities } from '../data/entities';
import { formatAed } from '../utils/format';
import { STAGE_META } from '../utils/stages';
import { cn } from '../utils/cn';
const complianceClasses: Record<string, string> = {
  'good-standing': 'text-uae-leaf',
  'under-review': 'text-[#8a6f11]',
  restricted: 'text-uae-red'
};
export function Registry() {
  const {
    applications
  } = usePlatform();
  const [query, setQuery] = React.useState('');
  const [selectedId, setSelectedId] = React.useState(entities[0].id);
  const filtered = entities.filter(entity => entity.name.toLowerCase().includes(query.trim().toLowerCase()));
  const selected = entities.find(entity => entity.id === selectedId) ?? entities[0];
  const history = applications.filter(record => record.entityId === selected.id);
  return <div data-id="element-1148" data-mp="2n97ti3p03lmh">
      <PageHeader eyebrow="Administration" title="Entities registry" description="Federal, local government, semi-government and private organisers that apply for event support, with their bid history and compliance standing." data-id="element-1149" data-mp="2n9hk9thvdwt8" />

      <div className="grid gap-4 p-6 xl:grid-cols-[380px_1fr]" data-id="element-1150" data-mp="28klek33acvvs">
        <div className="space-y-3" data-id="element-1151" data-mp="28j0ha5pl6yu8">
          <Input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search entities" aria-label="Search entities" data-id="element-1152" data-mp="24k1hezipps9n" />
          <div className="space-y-2" data-id="element-1153" data-mp="1uksjxwon1vij">
            {filtered.map(entity => <button key={entity.id} type="button" onClick={() => setSelectedId(entity.id)} aria-current={selected.id === entity.id} className={cn('w-full rounded-sm border px-3 py-2.5 text-left transition-colors', selected.id === entity.id ? 'border-uae-green bg-uae-green text-white' : 'border-border bg-card hover:bg-muted')} data-id="element-1154" data-mp="1kxzn9qrcl3q4">
                <p className="text-[13px] font-medium leading-snug" data-id="element-1155" data-mp="28nk7ecz8m70z">{entity.name}</p>
                <p className={cn('mt-0.5 text-[11px]', selected.id === entity.id ? 'text-white/70' : 'text-muted-foreground')} data-id="element-1156" data-mp="1tyo56wjv3o8a">
                  {entity.kind} · {entity.emirate}
                </p>
              </button>)}
            {filtered.length === 0 && <p className="rounded-sm border border-dashed border-border px-3 py-6 text-center text-[12px] text-muted-foreground" data-id="element-1157" data-mp="2rfmti3unefim">
                No entities match “{query}”.
              </p>}
          </div>
        </div>

        <div className="space-y-4" data-id="element-1158" data-mp="pow27ja9dmie">
          <SectionCard title={selected.name} description={`${selected.kind} · ${selected.emirate}`} data-id="element-1159" data-mp="po30wxu2jp7n">
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1160" data-mp="kqbcwewjon8q">
              <div data-id="element-1161" data-mp="3t6e9npm4cria">
                <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1162" data-mp="3t5v0oyeg5dge">
                  Bid success rate
                </dt>
                <dd className="mt-1 font-mono text-lg font-semibold" data-id="element-1163" data-mp="60dmdsb57lgd">
                  {selected.bidsSubmitted === 0 ? '—' : `${Math.round(selected.bidsWon / selected.bidsSubmitted * 100)}%`}
                </dd>
                <dd className="text-[11px] text-muted-foreground" data-id="element-1164" data-mp="1hdz8f4lph6xo">
                  {selected.bidsWon} of {selected.bidsSubmitted} bids
                </dd>
              </div>
              <div data-id="element-1165" data-mp="28poupm9gfexz">
                <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1166" data-mp="28q6mo6p33703">
                  Support received
                </dt>
                <dd className="mt-1 font-mono text-lg font-semibold" data-id="element-1167" data-mp="2hoin10fr5j5q">
                  {formatAed(selected.supportReceivedAed)}
                </dd>
              </div>
              <div data-id="element-1168" data-mp="2mlsbodvbhqsi">
                <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1169" data-mp="2rnhpu2du6kkt">
                  Compliance standing
                </dt>
                <dd className={cn('mt-1 text-lg font-semibold capitalize', complianceClasses[selected.compliance])} data-id="element-1170" data-mp="2vn08eri6qjkh">
                  {selected.compliance.replace('-', ' ')}
                </dd>
              </div>
              <div data-id="element-1171" data-mp="3abmmkpw2t5a4">
                <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1172" data-mp="3fctvis9ygaid">
                  Contact
                </dt>
                <dd className="mt-1 text-[13px]" data-id="element-1173" data-mp="13qia0vqqnliy">{selected.contact}</dd>
              </div>
            </dl>
          </SectionCard>

          <SectionCard title="Application history" description="Every record submitted by this entity" bodyClassName="p-0" data-id="element-1174" data-mp="ysgpe6dnze5d">
            {history.length === 0 ? <p className="px-4 py-8 text-center text-[12px] text-muted-foreground" data-id="element-1175" data-mp="oukhdng0wzbh">
                This entity has no applications in the current environment.
              </p> : <div className="overflow-x-auto" data-id="element-1176" data-mp="1hj7ugvqmcidy">
                <Table data-id="element-1177" data-mp="1cp3mc95ma4ba">
                  <TableHeader data-id="element-1178" data-mp="1cokbtdiaqi7a">
                    <TableRow data-id="element-1179" data-mp="17r24tsnuad1x">
                      <TableHead className="w-[150px]" data-id="element-1180" data-mp="17rtzn19m46r5">Reference</TableHead>
                      <TableHead className="min-w-[220px]" data-id="element-1181" data-mp="25668lmr9ne8p">Event</TableHead>
                      <TableHead data-id="element-1182" data-mp="258algu757nl4">Stage</TableHead>
                      <TableHead className="text-right" data-id="element-1183" data-mp="20eoixuup1031">Support</TableHead>
                      <TableHead className="text-right" data-id="element-1184" data-mp="3untpww931xsa">Score</TableHead>
                      <TableHead data-id="element-1185" data-mp="3upxp50w1e8m8">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-id="element-1186" data-mp="3kxrxjs9p5t7k">
                    {history.map(record => <TableRow key={record.id} data-id="element-1187" data-mp="3kvnwdi36n8az">
                        <TableCell className="font-mono text-[11px] text-muted-foreground" data-id="element-1188" data-mp="3pw38657tg7qu">
                          {record.ref}
                        </TableCell>
                        <TableCell data-id="element-1189" data-mp="3fyp6jbqjxnil">
                          <Link to={`/applications/${record.id}`} className="text-[13px] hover:underline" data-id="element-1190" data-mp="3fzqg1brx61y7">
                            {record.eventName}
                          </Link>
                        </TableCell>
                        <TableCell className="text-[12px]" data-id="element-1191" data-mp="108knj09q1tye">
                          {STAGE_META[record.stage].label}
                        </TableCell>
                        <TableCell className="text-right font-mono text-[12px]" data-id="element-1192" data-mp="2poof7sjzsq3j">
                          {formatAed(record.requestedSupportAed)}
                        </TableCell>
                        <TableCell className="text-right" data-id="element-1193" data-mp="2knh8lwtl5wyg">
                          <ScoreChip score={record.aiScore} data-id="element-1194" data-mp="2zfj9nk7e6w02" />
                        </TableCell>
                        <TableCell data-id="element-1195" data-mp="2umq6h0rknvq3">
                          <RecordStatusPill status={record.status} data-id="element-1196" data-mp="2ulom25oo1wvp" />
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </div>}
          </SectionCard>
        </div>
      </div>
    </div>;
}